import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Send, Check, User, Phone, MessageSquare, 
  GraduationCap, Award, Users, CheckCircle, Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import Layout from '../layout/Layout';
import { contactAPI } from '../../lib/api';

const DemandeInfoPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await contactAPI.sendDemandeInfo(formData);
      setSubmitted(true);
      toast({ 
        title: 'Demande envoyée !', 
        description: 'Nous vous recontacterons rapidement.'
      });
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: 'Une erreur est survenue. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-[#285BBB] to-[#2C3C65] flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2C3C65] mb-4">Demande envoyée !</h2>
            <p className="text-[#6777A1] mb-6">
              Merci pour votre intérêt ! Notre équipe vous recontactera dans les plus brefs délais 
              pour répondre à toutes vos questions.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-[#285BBB] text-white font-semibold rounded-lg hover:bg-[#1e4a9a] transition-colors"
            >
              Retour à l'accueil
            </Link>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50" data-testid="demande-info-page">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white text-sm font-medium mb-4">
                <Info className="h-4 w-4" />
                Centre agréé Cambridge
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Demande d'informations
              </h1>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Vous avez des questions ? Notre équipe vous répond sous 24h
              </p>
            </motion.div>
          </div>
        </section>

        {/* Information importante */}
        <section className="py-6 bg-amber-50 border-b border-amber-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">Cours d'évaluation gratuit</h3>
                <p className="text-amber-700 text-sm">
                  Pour devenir élève, un cours d'évaluation gratuit est nécessaire afin de définir votre niveau. 
                  Mentionnez-le dans votre message et nous vous proposerons un créneau.
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Formulaire */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl shadow-xl p-8"
              >
                <h2 className="text-2xl font-bold text-[#2C3C65] mb-6">Envoyez-nous votre demande</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                      <Input
                        required
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        placeholder="Votre nom"
                        className="h-12"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                      <Input
                        required
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                        placeholder="Votre prénom"
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone *</label>
                    <Input
                      type="tel"
                      required
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      placeholder="06 XX XX XX XX"
                      className="h-12"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Votre message *</label>
                    <Textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Décrivez votre demande, vos questions, ou demandez un cours d'évaluation gratuit..."
                      rows={5}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-[#ff3a2d] hover:bg-[#e6352a] text-white font-bold text-lg h-14"
                    data-testid="submit-demande-info"
                  >
                    {loading ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        <Send className="h-5 w-5 mr-2" />
                        Envoyer ma demande
                      </>
                    )}
                  </Button>
                </form>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Avantages */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl shadow-xl p-6"
              >
                <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Pourquoi nous choisir ?</h3>
                <ul className="space-y-4">
                  {[
                    { icon: GraduationCap, text: 'Centre agréé Cambridge' },
                    { icon: Award, text: 'Certifié Qualiopi' },
                    { icon: Users, text: 'Formateurs natifs' },
                    { icon: CheckCircle, text: 'Évaluation gratuite' },
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#F5F7FF] rounded-lg flex items-center justify-center">
                        <item.icon className="h-5 w-5 text-[#285BBB]" />
                      </div>
                      <span className="text-[#2C3C65] font-medium">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Contact rapide */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-[#285BBB] to-[#2C3C65] rounded-2xl shadow-xl p-6 text-white"
              >
                <h3 className="text-lg font-semibold mb-4">Contact direct</h3>
                <p className="text-blue-100 text-sm mb-4">
                  Vous préférez nous contacter directement ?
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+33626716022"
                    className="flex items-center gap-3 text-white hover:text-blue-200 transition-colors"
                  >
                    <Phone className="h-5 w-5" />
                    <span>06 26 71 60 22</span>
                  </a>
                </div>
              </motion.div>

              {/* Niveaux Cambridge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-2xl shadow-xl p-6"
              >
                <h3 className="text-lg font-semibold text-[#2C3C65] mb-4">Nos formations</h3>
                <p className="text-sm text-[#6777A1] mb-4">
                  Le niveau est défini par votre maîtrise de l'anglais, pas par votre âge.
                </p>
                <div className="space-y-2">
                  {[
                    { level: 'Pre A1 - A2', name: 'Starters, Movers, Flyers', color: 'bg-green-100 text-green-700' },
                    { level: 'A2 - B2', name: 'Key, Preliminary, First', color: 'bg-blue-100 text-blue-700' },
                    { level: 'B1 - C2', name: 'Advanced, Proficiency', color: 'bg-purple-100 text-purple-700' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-[#2C3C65]">{item.name}</span>
                      <span className={`text-xs px-2 py-1 rounded ${item.color}`}>{item.level}</span>
                    </div>
                  ))}
                </div>
                <Link 
                  to="/cambridge" 
                  className="block mt-4 text-center text-sm text-[#285BBB] font-medium hover:underline"
                >
                  En savoir plus sur les niveaux →
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DemandeInfoPage;
